// © Microsoft Corporation. All rights reserved.

import { SocialMedia, SocialMediaTarget } from "@msnews/views-helpers";

import { CardContextMenuItem } from "@msnews/card-context-menu";
import { DialogArticleCardProps } from "../dialog-article-card";
import { GlyphType } from "@msnews/icon";
import { ManagedClasses } from "@microsoft/fast-components-class-name-contracts-msft";
import { ShareDialogProps } from "@msnews/share-dialog";
import { SharingToolbarClassNames } from "./";
import { Status } from "../../ContentActionsToolbar.constants";
import { TelemetryTags } from "@msnews/experiences-base";
import { ToolbarPosition } from "../..";

/**
 * Enumeration of available button type options
 * @type {enum}
 */
export enum ButtonType {
    SocialMedia = "SocialMedia",
    ShareDialog = "ShareDialog",
    LikeDislike = "LikeDislike",
    MoreOptions = "MoreOptions"
}

/**
 * Coordinate position type
 */
export type CoordinatePos = {
    /**
     * A horizontal coordinate position
     * @type {number}
     */
    x: number;

    /**
     * A vertical coordinate position
     * @type {number}
     */
    y: number;
};

/**
 * The social media type button
 */
export type SocialMediaType = {
    /**
     * Optional HTML anchor attributes
     * @type {React.HTMLAttributes<HTMLAnchorElement>}
     */
    attributes?: React.HTMLAttributes<HTMLAnchorElement>;

    /**
     * The social media enum type
     * @type {SocialMedia}
     */
    mediaType: SocialMedia;

    /**
     * The shared content url
     * @type {string}
     */
    contentUrl: string;

    /**
     * The shared content title
     * @type {string}
     */
    contentTitle: string;

    /**
     * The localized email body text
     * @type {string}
     */
    emailBody?: string;

    /**
     * The icon to be used instead of default one
     * @type {GlyphType}
     */
    icon?: GlyphType;

    /**
     * The ocid value to be used
     * @type  {string}
     */
    ocid?: string;

    /**
     * The social media anchor target attribute option to be used
     * @type {SocialMediaTarget}
     */
    target?: SocialMediaTarget;

    /**
     * The telemetry callback to triggered when new window is used as the anchor target
     * @type {(event: any) => void}
     */
    newWindowTelemetryCall?: (event: any) => void;
};

/**
 * The share dialog type button
 */
export type ShareDialogType = {
    /**
     * Optional HTML button attributes
     * @type {React.HTMLAttributes<HTMLButtonElement>}
     */
    attributes?: React.HTMLAttributes<HTMLButtonElement>;

    /**
     * The share dialog component props
     * @type {ShareDialogProps}
     */
    shareDialogProps: ShareDialogProps;

    /**
     * The dialog article card component props
     * @type {DialogArticleCardProps}
     */
    dialogArticleCardProps: DialogArticleCardProps;

    /**
     * The telemetry tags
     * @type {TelemetryTags}
     */
    articleTelemetryTags?: TelemetryTags;

    /**
     * Callback to be triggered at user click
     * @type {(event: any) => void}
     */
    onSelect?: (event: any) => void;
};

/**
 * The like/dislike type button
 */
export type LikeDislikeType = {
    /**
     * Optional HTML button attributes
     * @type {React.HTMLAttributes<HTMLButtonElement>}
     */
    attributes?: React.HTMLAttributes<HTMLButtonElement>;

    /**
     * Current status (like/dislike/none) of the content
     * @type {Status}
     */
    status: Status;

    /**
     * Callback to be triggered at user click
     * @type {(event: any) => void}
     */
    onSelect?: (event: any) => void;
};

/**
 * The more options type button
 */
export type MoreOptionsType = {
    /**
     * Optional HTML div attributes
     * @type {React.HTMLAttributes<HTMLDivElement>}
     */
    attributes?: React.HTMLAttributes<HTMLDivElement>;

    /**
     * Current status (like/dislike/none) of the content
     * @type {Status}
     */
    status: Status;

    /**
     * The array of items to be displayed in the menu
     * @type {CardContextMenuItem[]}
     */
    menuItems: CardContextMenuItem[];

    /**
     * The offset position of the menu to open in relation to its button
     * Default to (0,0)
     * @type {CoordinatePos}
     */
    offsetPos?: CoordinatePos;

    /**
     * The override zIndex to be used by the menu
     * @type {number}
     */
    zIndex?: number;
};

/**
 * The button item common type
 */
export type ButtonItemType = {
    /**
     * The content actions toolbar button type
     * @type {ButtonType}
     */
    buttonType: ButtonType;

    /**
     * The content actions toolbar button data type
     * @type {ButtonDataType}
     */
    buttonData?: ButtonDataType;

    /**
     * The telemetry tags
     * @type {TelemetryTags}
     */
    telemetryTags?: TelemetryTags;
};

export type ButtonDataType = Partial<SocialMediaType & ShareDialogType & LikeDislikeType & MoreOptionsType>;

/**
 * An interface to represent the SharingToolbar jss managed classes.
 *
 * @export
 * @interface SharingToolbarManagedClasses
 */
export type SharingToolbarManagedClasses = ManagedClasses<SharingToolbarClassNames>;

/**
 * An interface to represent the SharingToolbar handled props.
 *
 * @export
 * @interface SharingToolbarHandledProps
 */
export interface SharingToolbarHandledProps extends SharingToolbarManagedClasses {

    /**
     * The toolbar position (vertical/horizontal)
     * @type {ToolbarPosition}
     */
    position: ToolbarPosition;

    /**
     * The sharing buttons configuration array
     * @type {ButtonItemType[]}
     */
    sharingButtons: ButtonItemType[];
}

/**
 * An interface to represent the SharingToolbar unhandled props.
 * @export
 * @interface SharingToolbarUnHandledProps
 */
export type SharingToolbarUnHandledProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * The SharingToolbar props type
 */
export type SharingToolbarProps = SharingToolbarHandledProps & SharingToolbarUnHandledProps;